import { Box } from "@mui/material";
import React from "react";

function formatDate(epoch) {
  return new Date(epoch * 1000).toLocaleString("de-DE");
}

function groupByVat(amounts) {
  const grouped = {};
  for (const entry of amounts) {
    const key = entry.vat_definition_export_id;
    if (!grouped[key]) {
      grouped[key] = { incl_vat: 0, excl_vat: 0, vat: 0 };
    }
    grouped[key].incl_vat += entry.incl_vat;
    grouped[key].excl_vat += entry.excl_vat;
    grouped[key].vat += entry.vat;
  }
  return grouped;
}

function aggregateBy(transactions, getKey, filterFn = () => true) {
  const map = new Map();
  for (const tx of transactions.filter(filterFn)) {
    for (const line of tx.data.lines || []) {
      const key = getKey(tx, line);
      const amount = line.business_case.amounts_per_vat_id.reduce((sum, a) => sum + a.incl_vat, 0);
      map.set(key, (map.get(key) || 0) + amount);
    }
  }
  return Array.from(map.entries());
}

const DisplayZBericht = ({ data }) => {

  if (!data?.cash_point_closing) return <p>Kein Z-Bericht vorhanden.</p>;

  const closing = data.cash_point_closing;
  const cashStatement = closing.cash_statement;
  const transactions = closing.transactions.filter(e=>e.head.type==='Beleg');

  const byProduct = aggregateBy(transactions, (tx, line) => line.text);
  const byClient = aggregateBy(transactions, (tx) => tx.head.closing_client_id || "Unbekannt");
  const byStaff = aggregateBy(transactions, (tx) => tx.head?.user?.name || "Unbekannt");
  const byPurchaserAgency = aggregateBy(
    transactions,
    (tx, line) => line.business_case?.purchaser_agency_id || "Ohne Kundengruppe",

  );

  const stornos = transactions.filter(tx => tx.head.storno);
  const stornoByStaff = aggregateBy(transactions, (tx) => tx.head.closing_user_id || "Unbekannt", tx => tx.head.storno);
  const stornoSum = stornos.reduce((sum, tx) => sum + tx.data.full_amount_incl_vat, 0);

  return (
    <Box>
      <div className="zbon-print" style={{ fontFamily: "monospace", padding: 0, fontSize: "10px", lineHeight: 1.1 }}>
        <style>
          {`.zbon-print p { margin: 0; padding: 0 0 2px 0; }`}
          {`.zbon-print h2 { margin: 0; padding: 8px 0 4px 0; }`}
          {`.zbon-print ul { margin: 0; padding: 2px 0 2px 20px; }`}

          {`
          @media print {
            body * { visibility: hidden; }
            .zbon-print, .zbon-print * { visibility: visible; }
            .zbon-print { position: absolute; left: 0; top: 0; width: 100%; }
          }
        `}
        </style>


        <h1>Z-Bericht</h1>

        <section>
          <h2>Kopf</h2>
          <p><strong>Erstellungsdatum:</strong> {formatDate(closing.head.export_creation_date)}</p>
          <p><strong>Export Bon ID Bereich:</strong> {closing.head.first_transaction_export_id} - {closing.head.last_transaction_export_id}</p>
          <p><strong>Export Bon ID Bereich:</strong> {formatDate(transactions[0].head.timestamp_start)} - {formatDate(transactions[transactions.length - 1].head.timestamp_end)}</p>
          <p><strong>Client ID:</strong> {closing.client_id}</p>
          <p><strong>Z-Bericht Export ID:</strong> {closing.cash_point_closing_export_id}</p>
        </section>

        <section>
          <h2>Umsätze nach USt-Gruppen</h2>
          {cashStatement.business_cases.map((bc, i) => {
            const grouped = groupByVat(bc.amounts_per_vat_id);
            return (
              <div key={i}>
                <p><strong>{bc.type} - {bc.name}</strong></p>
                {Object.entries(grouped).map(([vatId, amt], j) => (
                  <ul key={j}>
                    <li>USt-ID: {vatId}</li>
                    <li>Brutto: {amt.incl_vat.toFixed(2)} EUR</li>
                    <li>Nettobetrag: {amt.excl_vat.toFixed(2)} EUR</li>
                    <li>Umsatzsteuer: {amt.vat.toFixed(2)} EUR</li>
                  </ul>
                ))}
              </div>
            );
          })}
        </section>

        <section>
          <h2>Zahlungen</h2>
          <p><strong>Gesamt:</strong> {cashStatement.payment.full_amount.toFixed(2)} EUR</p>
          <p><strong>Bar:</strong> {cashStatement.payment.cash_amount.toFixed(2)} EUR</p>
          <p><strong>Zahlarten:</strong></p>
          <ul>
            {cashStatement.payment.payment_types.map((pt, i) => (
              <li key={i}>{pt.name} ({pt.type}): {pt.amount.toFixed(2)} {pt.currency_code}</li>
            ))}
          </ul>
        </section>

<hr />

        <section>
          <h2>Stornos (gesamt)</h2>
          <p>Summe: {stornoSum.toFixed(2)} EUR</p>
          <ul>
            {stornoByStaff.map(([staff, sum], i) => (
              <li key={i}>{staff}: {sum.toFixed(2)} EUR</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>Nach Produkt</h2>
          <ul>
            {byProduct.map(([product, sum], i) => (
              <li key={i}>{product}: {sum.toFixed(2)} EUR</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>Nach POS / Client</h2>
          <ul>
            {byClient.map(([client, sum], i) => (
              <li key={i}>{client}: {sum.toFixed(2)} EUR</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>Nach Mitarbeiter</h2>
          <ul>
            {byStaff.map(([staff, sum], i) => (
              <li key={i}>{staff}: {sum.toFixed(2)} EUR</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>Nach Purchaser Agency</h2>
          <ul>
            {byPurchaserAgency.map(([agencyId, sum], i) => (
              <li key={i}>{agencyId}: {sum.toFixed(2)} EUR</li>
            ))}
          </ul>
        </section>

        {/**
 * 
 
      <section>
        <h2>Transaktionen</h2>
        {transactions.map((tx, i) => {
          const grouped = groupByVat(tx.data.amounts_per_vat_id);
          return (
            <div key={i} style={{ borderTop: "1px solid #ccc", marginTop: 10, paddingTop: 10 }}>
              <h3>
                Bon #{tx.head.transaction_export_id} {tx.head.storno && "(STORNO)"}
              </h3>
              <p><strong>TSE Id:</strong> {tx.head.number}</p>
              <p><strong>Typ:</strong> {tx.head.type}</p>
              <p><strong>Beginn:</strong> {formatDate(tx.head.timestamp_start)}</p>
              <p><strong>Ende:</strong> {formatDate(tx.head.timestamp_end)}</p>
              {tx.head.storno && tx.head.references?.length > 0 && (
                <div>
                  <p><strong>Bezug auf Transaktion(en):</strong></p>
                  <ul>
                    {tx.head.references.map((ref, r) => (
                      <li key={r}>
                        Export-ID: {ref.transaction_export_id} (Kassenabschluss: {ref.cash_point_closing_export_id})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p><strong>Gesamtbetrag (inkl. USt.):</strong> {tx.data.full_amount_incl_vat.toFixed(2)} EUR</p>
              <p><strong>Umsätze nach USt-Gruppen:</strong></p>
              {Object.entries(grouped).map(([vatId, amt], j) => (
                <ul key={j}>
                  <li>USt-ID: {vatId}</li>
                  <li>Brutto: {amt.incl_vat.toFixed(2)} EUR</li>
                  <li>Nettobetrag: {amt.excl_vat.toFixed(2)} EUR</li>
                  <li>Umsatzsteuer: {amt.vat.toFixed(2)} EUR</li>
                </ul>
              ))}
              <p><strong>Zahlarten:</strong></p>
              <ul>
                {tx.data.payment_types.map((pt, j) => (
                  <li key={j}>{pt.type}: {pt.amount.toFixed(2)} {pt.currency_code}</li>
                ))}
              </ul>
              <p><strong>Artikel:</strong></p>
              <ul>
                {tx.data.lines.map((line, k) => (
                  <li key={k}>
                    {line.text} – {line.item.quantity} {line.item.quantity_measure} x {line.item.price_per_unit.toFixed(2)} EUR
                    {line.storno && " (STORNIERT)"}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>
      */}

      </div>
      <button onClick={() => window.print()} style={{ marginTop: 20 }}>Drucken</button>
    </Box>
  );
};

export default DisplayZBericht;




